import get from 'lodash/get';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SelectDocumentInGroup from '~/components/SearchV2/AddSearchResultsToGroupV2/SelectDocumentInGroup/SelectDocumentInGroup';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { AddToGroupStepType } from '~/enums/AddToGroupType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { MODAL_DOCUMENT_GROUP_RELATION } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';

const MAX_TITLE_WIDTH = 355;

const LinkToParent = ({ groupId, onComplete }) => {
  const step = AddToGroupStepType.STRUCTURE;
  const intl = useIntl();
  const documentId = useSelector(
    slices.documentGroup.selectors.selectDocumentId,
  );
  const modal = useSelector(slices.documentGroup.selectors.selectModal);
  const { modalType, context } = modal || {};

  const hideModal = () => {
    dispatch(slices.documentGroup.actions.setModal());
    dispatch(slices.documentGroup.actions.setDocumentId(undefined));
  };

  const dispatch = useDispatch();

  const [linkDocumentsToGroup, linkDocumentsToGroupResult] =
    api.endpoints.linkDocumentsToGroup.useMutation();
  const {
    isError: linkDocumentsToGroupError,
    isSuccess: linkDocumentsToGroupSuccess,
    isLoading: linkDocumentsToGroupLoading,
    error: linkDocumentsToGroupResponseError,
  } = linkDocumentsToGroupResult;

  useEffect(() => {
    if (linkDocumentsToGroupSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'LinkToParent.linkSuccess',
          defaultMessage: 'Documents added to the group successfully!',
          description: 'Success message when documents are linked to group',
        }),
      );
      hideModal();
      onComplete?.();
    }
  }, [linkDocumentsToGroupSuccess, intl]);

  useEffect(() => {
    if (linkDocumentsToGroupError) {
      const { response } = linkDocumentsToGroupResponseError;
      const defaultErrorMessage = intl.formatMessage({
        id: 'LinkToParent.linkError',
        defaultMessage:
          'Error on adding documents to the group, please try again later.',
        description: 'Default error message when document linking fails',
      });
      if (response && response.status === HttpStatusCodeType.BadRequest) {
        showToast(ERROR, get(response, 'data.message', defaultErrorMessage));
      } else {
        showToast(ERROR, defaultErrorMessage);
      }
      hideModal();
    }
  }, [linkDocumentsToGroupError]);

  const handleSelectedDocument = useCallback((documentId) => {
    dispatch(slices.documentGroup.actions.setDocumentId(documentId));
  }, []);

  const handleLinkDocuments = () => {
    if (documentId) {
      linkDocumentsToGroup({
        groupId,
        parentId: documentId,
        //TODO: remove once "load more" is not selectable anymore
        children: context?.selectedRowIds?.filter(
          (docGroupId) => !isNaN(docGroupId),
        ),
      });
    }
    dispatch(slices.documentGroup.actions.setDocumentId(undefined));
  };

  const steps = useMemo(() => {
    return {
      [AddToGroupStepType.STRUCTURE]: {
        title: intl.formatMessage({
          id: 'LinkToParent.linkingTitle',
          defaultMessage: 'Linking documents to parent file',
          description: 'Title for linking documents to parent file step',
        }),
        component: (
          <SelectDocumentInGroup
            group={groupId}
            onDocumentSelected={handleSelectedDocument}
            documentGroupIds={context?.selectedRowIds}
            autoSelectOnlyOption={true}
            title={intl.formatMessage({
              id: 'LinkToParent.selectParentTitle',
              defaultMessage: 'Select parent file to link to',
              description: 'Title for selecting parent file',
            })}
          />
        ),
      },
    };
  }, [groupId, context]);

  const isLoading = linkDocumentsToGroupLoading;

  if (modalType !== MODAL_DOCUMENT_GROUP_RELATION) return null;

  return (
    <WizardPageLayout
      currentStep={AddToGroupStepType.STRUCTURE}
      steps={[AddToGroupStepType.STRUCTURE]}
      title={{ text: steps[step].title, maxWidth: MAX_TITLE_WIDTH }}
      onClose={hideModal}
      actionButton={{
        ...steps[step].action,
        text: intl.formatMessage({
          id: 'LinkToParent.linkDocumentsButton',
          defaultMessage: 'Link Documents',
          description: 'Button text for linking documents',
        }),
        promise: handleLinkDocuments,
        disabled: !documentId,
      }}
      isContentFill={true}
    >
      <LoadingContainer isLoading={isLoading}>
        {steps[step].component}
      </LoadingContainer>
    </WizardPageLayout>
  );
};

export default LinkToParent;
